// Recipe-components helpers

import {
  CreateRecipeComponentDTO,
  IngredientEntity,
  Recipe,
  RecipeComponentFormValue,
  RecipeSectionFormValue,
} from "../types/recipe";
import {
  findUnit,
  isGramUnit,
  parseMeasurementUnits,
  toComponentAmount,
} from "./measurementUnits";

export interface RecipeMacroTotals {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
  sodium_mg: number;
}

type IngredientPer100gColumn =
  | "calories_kcal_per_100g"
  | "protein_g_per_100g"
  | "carbs_g_per_100g"
  | "fat_g_per_100g"
  | "fiber_g_per_100g"
  | "sodium_mg_per_100g";

const FIELD_TO_COLUMN: Record<
  keyof RecipeMacroTotals,
  IngredientPer100gColumn
> = {
  calories: "calories_kcal_per_100g",
  protein: "protein_g_per_100g",
  carbs: "carbs_g_per_100g",
  fat: "fat_g_per_100g",
  fiber: "fiber_g_per_100g",
  sodium_mg: "sodium_mg_per_100g",
};

export const ZERO_MACROS: RecipeMacroTotals = Object.freeze({
  calories: 0,
  protein: 0,
  carbs: 0,
  fat: 0,
  fiber: 0,
  sodium_mg: 0,
});

export const DEFAULT_SECTION_NAME = "Main";

export const SERVINGS_MIN = 1;
// API: RecipeStoreRequest / RecipeUpdateRequest `default_num_of_servings` max:300.
export const SERVINGS_MAX = 300;
export const NAME_MAX = 255;
export const DESCRIPTION_MAX = 1000;
export const SECTION_NAME_MAX = 255;
export const PORTION_MIN_G = 0.01;
export const PORTION_MAX_G = 100000;
export const PORTION_MAX_DECIMALS = 2;
export const NOTE_MAX = 500;
export const STEP_TEXT_MAX = 250;

export const clampPortion = (value: number): number => {
  if (!Number.isFinite(value) || value <= 0) return 0;
  const rounded = Math.round(value * 100) / 100;
  return Math.min(PORTION_MAX_G, rounded);
};

// Cap a free-text amount field to 2 decimals / max, keeping a trailing dot
// while typing.
export const limitPortionText = (
  text: string,
  max: number = PORTION_MAX_G
): string => {
  let t = text.replace(/[^\d.]/g, "");
  const dot = t.indexOf(".");
  if (dot !== -1) {
    const intPart = t.slice(0, dot);
    const decPart = t
      .slice(dot + 1)
      .replace(/\./g, "")
      .slice(0, PORTION_MAX_DECIMALS);
    t = `${intPart}.${decPart}`;
  }
  const n = Number(t);
  if (Number.isFinite(n) && n > max) return String(max);
  return t;
};

let sectionKeyCounter = 0;
export const makeSectionKey = (): string => {
  sectionKeyCounter += 1;
  return `sec-${Date.now().toString(36)}-${sectionKeyCounter.toString(
    36
  )}-${Math.random().toString(36).slice(2, 8)}`;
};

export const makeDefaultSection = (): RecipeSectionFormValue => ({
  id: makeSectionKey(),
  name: DEFAULT_SECTION_NAME,
});

let componentKeyCounter = 0;
export const makeComponentKey = (): string => {
  componentKeyCounter += 1;
  return `cmp-${Date.now().toString(36)}-${componentKeyCounter.toString(
    36
  )}-${Math.random().toString(36).slice(2, 8)}`;
};

// Nutrition always comes from the grams; the unit pair is display only.
export const computeComponentContribution = (
  component: RecipeComponentFormValue
): RecipeMacroTotals | null => {
  const multiplier = (component.portion_grams || 0) / 100;
  if (!Number.isFinite(multiplier) || multiplier <= 0) return null;

  const food = component.food;
  const contribution: RecipeMacroTotals = { ...ZERO_MACROS };
  for (const field of Object.keys(
    FIELD_TO_COLUMN
  ) as (keyof RecipeMacroTotals)[]) {
    const column = FIELD_TO_COLUMN[field];
    const raw = food[column];
    if (typeof raw === "number" && Number.isFinite(raw)) {
      contribution[field] = raw * multiplier;
    }
  }
  return contribution;
};

export const computeLivePreviewMacros = (
  components: RecipeComponentFormValue[]
): RecipeMacroTotals => {
  const totals: RecipeMacroTotals = { ...ZERO_MACROS };
  for (const component of components) {
    const contribution = computeComponentContribution(component);
    if (!contribution) continue;
    for (const field of Object.keys(
      FIELD_TO_COLUMN
    ) as (keyof RecipeMacroTotals)[]) {
      totals[field] += contribution[field];
    }
  }
  return totals;
};

// Per-serving = total / servings
export const dividePerServing = (
  totals: RecipeMacroTotals,
  servings: number
): RecipeMacroTotals => {
  const divisor = Number.isFinite(servings) && servings > 0 ? servings : 1;
  return {
    calories: totals.calories / divisor,
    protein: totals.protein / divisor,
    carbs: totals.carbs / divisor,
    fat: totals.fat / divisor,
    fiber: totals.fiber / divisor,
    sodium_mg: totals.sodium_mg / divisor,
  };
};

const toComponentDTO = (
  component: RecipeComponentFormValue,
  position: number,
  sectionName: string | null
): CreateRecipeComponentDTO => ({
  ingredient_id: component.food.id,
  ...toComponentAmount(component),
  position,
  section: sectionName,
});

// Section-major order with 1-based positions
export const buildComponentsDTO = (
  components: RecipeComponentFormValue[],
  sections: RecipeSectionFormValue[]
): CreateRecipeComponentDTO[] => {
  const out: CreateRecipeComponentDTO[] = [];
  let position = 1;

  for (const section of sections) {
    for (const component of components) {
      if (component.sectionId !== section.id) continue;
      out.push(toComponentDTO(component, position, section.name));
      position += 1;
    }
  }

  const knownSectionIds = new Set(sections.map((s) => s.id));
  for (const component of components) {
    if (component.sectionId && knownSectionIds.has(component.sectionId))
      continue;
    out.push(toComponentDTO(component, position, null));
    position += 1;
  }

  return out;
};

export const hydrateComponentsFromRecipe = (
  recipe: Recipe
): {
  components: RecipeComponentFormValue[];
  sections: RecipeSectionFormValue[];
} => {
  const sorted = (recipe.components ?? [])
    .slice()
    .sort((a, b) => a.position - b.position);

  const sectionsByName = new Map<string, RecipeSectionFormValue>();
  for (const c of sorted) {
    const name =
      c.section && c.section.length > 0 ? c.section : DEFAULT_SECTION_NAME;
    if (!sectionsByName.has(name)) {
      sectionsByName.set(name, { id: makeSectionKey(), name });
    }
  }
  const sections =
    sectionsByName.size > 0
      ? Array.from(sectionsByName.values())
      : [makeDefaultSection()];

  const components: RecipeComponentFormValue[] = [];
  for (const c of sorted) {
    if (!c.ingredient) continue;
    // The recipe endpoint returns the raw ingredient model; its unit list is
    // sanitised like the search rows.
    const food: IngredientEntity = {
      ...c.ingredient,
      measurement_units: parseMeasurementUnits(c.ingredient.measurement_units),
    };
    const sectionName =
      c.section && c.section.length > 0 ? c.section : DEFAULT_SECTION_NAME;
    // A unit the ingredient no longer offers falls back to the stored grams;
    // re-sending its label would be refused.
    const unit = findUnit(food.measurement_units, c.measurement_unit_label);
    const usesUnit =
      !!unit &&
      !isGramUnit(unit) &&
      typeof c.quantity === "number" &&
      Number.isFinite(c.quantity) &&
      c.quantity > 0;
    components.push({
      key: makeComponentKey(),
      food,
      portion_grams: c.portion_grams,
      quantity: usesUnit ? c.quantity : null,
      measurement_unit_label: usesUnit ? unit.label : null,
      sectionId: sectionsByName.get(sectionName)?.id ?? sections[0].id,
    });
  }

  return { components, sections };
};
